import type { ElementType, ReactNode } from 'react';
import { shared } from '../content/shared';
import { pendingOf, textOf, type Copy, type Cta, type LeadItem } from '../content/types';
import { isDev } from './siteEnv';
import s from '../site.module.css';

/**
 * A block whose copy waits on a decision. Production renders it plainly; in development it gets a dashed outline and
 * a "pending" tag naming the marker, so open decisions are visible while reviewing the page.
 */
export function Pending({ marker, as: Tag = 'div', className, children }: { marker?: string; as?: ElementType; className?: string; children: ReactNode }) {
  if (!marker || !isDev) return <Tag className={className}>{children}</Tag>;
  return (
    <Tag className={`${className ?? ''} ${s.pending}`} data-pending={marker}>
      <span className={s.pendingTag}>
        {shared.dev.pendingTag}: {marker}
      </span>
      {children}
    </Tag>
  );
}

/** Copy with its pending marker, rendered as the given element. */
export function CopyText({ copy, as = 'p', className }: { copy: Copy; as?: ElementType; className?: string }) {
  return (
    <Pending marker={pendingOf(copy)} as={as} className={className}>
      {textOf(copy)}
    </Pending>
  );
}

/** A value still to be supplied: a visible TODO in development, nothing in production. */
export function Todo({ label }: { label: string }) {
  if (!isDev) return null;
  return (
    <span className={s.todo} data-todo={label}>
      {shared.dev.todoTag}: {label}
    </span>
  );
}

/** Body text with the link's label (a substring of it) made a link; links to other origins open normally. */
export function Linked({ text, link }: { text: string; link?: Cta }) {
  if (!link) return <>{text}</>;
  const at = text.indexOf(link.label);
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <a href={link.href}>{link.label}</a>
      {text.slice(at + link.label.length)}
    </>
  );
}

/** "Lead in. | Body" list items: the lead in bold, then the body (with its link). */
export function LeadText({ item }: { item: LeadItem }) {
  return (
    <Pending marker={pendingOf(item.body)} as="span" className={s.leadItem}>
      <strong>{item.lead}</strong> <Linked text={textOf(item.body)} link={item.link} />
    </Pending>
  );
}
